import moment from 'moment';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { hashifyDictionary } from 'barnyard/utils/';
import { createSelector } from 'reselect';
import {
  PERMISSION_GROUPS, PATHWAY_THRIVE_CUSTOM_GROUPS, BUYER_PERMISSIONS_OBJECT,
  BUYER_PERMISSIONS_ALPHABETICAL, getFilteredPermissions
} from '~/app/common/constants/ClinicUserPermissions';
import { CategoryLevel } from '../constants';
import { CORPORATE_PERMISSIONS_READ } from '../../settings/constants';
import { getGuidanceRules } from '../../rules/rules';

/** *
  General Selectors to fetch top level items from the core store
***/
const getCoreEntities = state => state.entities;


/** *
  General Selectors to get params
***/
const getSupplierIdFromProps = (state, props) => props.supplierId;
const getValueFromProps = v => (state, props) => props[v];

/** *
  Selector to get corporate
***/
const getCorporate = createSelector(getCoreEntities, entities => entities.corporate);
const getCorporateDataSettings = createSelector(getCoreEntities, entities => entities.corporate_data_settings);
const getProfile = createSelector(getCoreEntities, entities => entities.profile);
const getEmail = createSelector(getProfile, profile => profile.email);
const getCorporateUser = createSelector(getCoreEntities, entities => entities.corporate_user);
const getCorporateRegions = createSelector(getCoreEntities, entities => entities.corporate_regions);
const getSearches = createSelector(getCoreEntities, entities => entities.searches);
const getBuyers = createSelector(getCoreEntities, entities => entities.buyers);
const getAllSuppliersHash = createSelector(getCoreEntities, entities => entities.suppliers);
const getSuppliers = createSelector(
  getAllSuppliersHash,
  entities => _.filter(entities, 'is_shoppable')
);
const getBuyerCorporateMetaOptions = createSelector(getCorporateDataSettings, settings => settings.buyer_corporate_meta_options);
const getDashboard = state => state.dashboard.dashboard;
export const getDashboardBuyers = createSelector(
  getDashboard,
  dashboard => _.values(_.get(dashboard, 'buyers', {}))
);
export const getDashboardFetches = state => state.dashboard.fetches;
const getDashboardBuyerLookup = createSelector(
  getDashboard,
  dashboard => _.get(dashboard, 'buyers', {})
);
const getBuyerIdFromProps = (state, props) => props.buyerId;
const getDashboardBuyer = createSelector(
  getBuyerIdFromProps,
  getDashboardBuyerLookup,
  (buyerId, buyers) => buyers[buyerId]
);

const getIsPathWayOrThrive = createSelector(
  getCorporate,
  corporate => (
    _.get(corporate, 'slug') === 'pathwayvetalliance'
    || _.get(corporate, 'slug') === 'thrive'
  )
);

const getFetches = state => state.fetches;
const getIsBuyersFetched = createSelector(getFetches, fetches => fetches.buyers);
const getIsSuppliersFetched = createSelector(getFetches, fetches => fetches.suppliers);
const getRegions = createSelector(getCorporateRegions, regions => _.values(regions));
const getEmails = createSelector(getCorporate, corporate => corporate.emails);
const getProductLines = createSelector(getCoreEntities, entities => entities.productlines || []);
const getGlCodes = createSelector(getCoreEntities, entities => entities.glcodes || []);
const getGlCodesAll = createSelector(getCoreEntities, entities => entities.glcodes_all || []);
const getGlCodesLookup = createSelector(getGlCodes, glcodes => _.keyBy(glcodes, gl => gl.id));
const getGlCodesAllLookup = createSelector(getGlCodesAll, glcodes => _.keyBy(glcodes, gl => gl.id));
const getGlCodesByKind = createSelector(getGlCodes, glcodes => _.groupBy(glcodes, gl => gl.code_kind_id));
const getCorporateCompanies = createSelector(getCorporate, corporate => corporate.companies || []);
const getCorporateCostCenters = createSelector(getCorporate, corporate => corporate.cost_centers || []);
const getManufacturers = createSelector(getCoreEntities, entities => entities.manufacturers);
const getGlCodesLookupById = createSelector(
  getValueFromProps('glCodeId'),
  getGlCodesLookup,
  (glCodeId, glcodes) => glcodes[glCodeId] || {}
);

const getGlCodesAllLookupById = createSelector(
  getValueFromProps('glCodeId'),
  getGlCodesAllLookup,
  (glCodeId, glcodes) => glcodes[glCodeId] || {}
);

const getGlCodesWithName = createSelector(
  getGlCodes,
  glcodes => _.filter(glcodes, 'name')
);

const getHasApprovalOrdersEnabled = createSelector(
  getCorporate,
  corporate => corporate.has_approval_orders_enabled
);

const getSupplierIdsFromProps = (state, props) => props.supplierIds;
const getSupplierNames = createSelector(
  getSupplierIdsFromProps,
  getAllSuppliersHash,
  (supplierIds, suppliers) => {
    if (!supplierIds || !suppliers) {
      return [];
    }
    return _.orderBy(_.map(supplierIds, id => suppliers[id].short_name), name => _.toLower(name)).join(', ');
  }
);

const getIsAdminView = createSelector(
  getProfile,
  profile => profile.is_admin_view
);

const getIsStaff = createSelector(
  getProfile,
  profile => profile.is_staff
);

const getIsStaffOrAdminView = createSelector(
  getIsAdminView,
  getIsStaff,
  (isAdminView, isStaff) => isAdminView || isStaff,
);

const getUISettings = createSelector(
  getProfile,
  profile => _.get(profile, 'ui_settings', {})
);

const getIsPunchoutAvailable = createSelector(
  getUISettings,
  settings => _.get(settings, 'clinic__has_punchout_available', false)
);

const getShowBudgets = createSelector(
  getUISettings,
  settings => _.get(settings, 'show_budgets', true)
);

const getCanOverReceive = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__clinics_can_over_receive', false)
);

const getCanViewReceivableWarnings = createSelector(
  getUISettings,
  settings => _.get(settings, 'can_view_receivable_warnings', false),
);

const getInviteFieldsRequirementInfo = createSelector(
  getUISettings,
  settings => ({
    isCogsCompanyIdRequired: _.get(settings, 'corporate__clinic_invite_cogs_company_id_required', false),
    isOpexCompanyIdRequired: _.get(settings, 'corporate__clinic_invite_opex_company_id_required', false),
    isDefaultCostCenterRequired: _.get(settings, 'corporate__clinic_invite_default_cost_center_required', false),
    isAcquistionDateRequired: _.get(settings, 'corporate__clinic_invite_acquisition_date_required', false),
    isCorporateLocationIdRequired: _.get(settings, 'corporate__clinic_invite_corporate_location_id_required', false),
    isRegionRequired: _.get(settings, 'corporate__clinic_invite_region_required', false),
    isDivisionRequired: _.get(settings, 'corporate__clinic_invite_division_required', false),
    isGroupRequired: _.get(settings, 'corporate__clinic_invite_group_required', false),
  })
);

const getHasSecondLocationId = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__has_multiple_location_ids', false)
);

const getAnalyzeStartDateParam = createSelector(
  getUISettings,
  (settings) => {
    const monthsAgo = _.get(settings, 'analyze_start_date_months_ago', 12);
    return { start_date: moment().subtract(monthsAgo, 'months').format('YYYY-MM-DD') };
  }
);

const getCorporateHasDataSyncingEnabled = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__has_data_syncing_enabled', false)
);

const getCorporateHasInvoiceStatementsEnabled = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__has_invoice_statements_enabled', false)
);

/** *
  Selector to get single entities
***/
const getSupplier = createSelector(
  getAllSuppliersHash,
  getSupplierIdFromProps,
  (suppliers, supplierId) => suppliers[supplierId]
);

const getRegion = createSelector(
  getCorporateUser,
  getCorporateRegions,
  (user, regions) => (
    user.corporate_region_id ? regions[user.corporate_region_id] : null
  ),
);

const getRequisitionApprovalPermission = createSelector(
  getCorporateUser,
  user => user.can_approve_requisitions
);

const getIsCorporateKind = createSelector(
  getCorporate,
  corporate => corporate.kind === 'CORPORATE'
);

const getSortedBuyersForOrdersInvoices = createSelector(
  getBuyers,
  getCorporate,
  (buyers, corporate) => {
    const sortedBuyers = _.orderBy(buyers, 'name');

    sortedBuyers.push({ id: -corporate.id, name: 'Unclaimed Accounts' });

    return sortedBuyers;
  }
);

const getBuyer = createSelector(
  getBuyers,
  getValueFromProps('buyerId'),
  (buyers, buyerId) => buyers[buyerId]
);

/**
  Selectors for categories
**/
const getCategories = createSelector(
  getCoreEntities,
  entities => entities.categories || []
);

const getSecondaryCategories = createSelector(
  getCategories,
  categories => _.filter(categories, category => category.level === CategoryLevel.SECONDARY)
);

const getUncodedCategories = createSelector(
  getSecondaryCategories,
  categories => _.filter(categories, category => !category.gl_code)
);
const getGroupedCategories = createSelector(
  getCoreEntities,
  getValueFromProps('parentOnly'),
  (entities, parentOnly) => {
    let { categories } = entities;
    if (parentOnly) {
      categories = _.filter(categories, ['parent_id', null]);
    }

    return _(categories)
      .groupBy(category => category.parent_id || category.id)
      .mapValues((values, id) => ({ name: entities.categories[id].name, id, categories: values }))
      .value();
  }
);

/**
 * Selectors for the searches
 */
const getInitialQueryFromProps = (__, props) => hashifyDictionary(props.queryParams);
const getManufacturerSearch = createSelector(getSearches, searches => searches.manufacturers);
const getManufacturerSearches = createSelector(getManufacturerSearch, search => search.searches);
const getManufacturerResults = createSelector(getManufacturerSearch, search => search.results);
const getMostRecentManufacturerSearchResults = createSelector(
  getManufacturerSearches,
  getManufacturerResults,
  (searches, results) => results[_.findLast(searches, search => results[search])] || []
);
const getFromQueryManufacturerSearchResults = createSelector(
  getInitialQueryFromProps,
  getManufacturerResults,
  (query, results) => results[query] || []
);
const getManufacturerSearchResults = createSelector(
  getMostRecentManufacturerSearchResults,
  getFromQueryManufacturerSearchResults,
  (recentResults, queryResults) => Object.assign(
    {},
    recentResults,
    queryResults
  )
);
const getManufacturerSearchQuery = createSelector(getManufacturerSearches, searches => _.last(searches));

/**
 * Selectors for product searches
 */
const getProductSearch = createSelector(getSearches, searches => searches.products || {});
const getProductSearches = createSelector(getProductSearch, search => search.searches || []);
const getProductResults = createSelector(getProductSearch, search => search.results || {});
const getMostRecentProductSearchResults = createSelector(
  getProductSearches,
  getProductResults,
  (searches, results) => results[_.findLast(searches, search => results[search])] || []
);
const getFromQueryProductSearchResults = createSelector(
  getInitialQueryFromProps,
  getProductResults,
  (query, results) => results[query] || []
);
const getProductSearchResults = createSelector(
  getMostRecentProductSearchResults,
  getFromQueryProductSearchResults,
  (recentResults, queryResults) => ({
    ...recentResults,
    ...queryResults
  })
);
const getProductSearchQuery = createSelector(getProductSearches, searches => _.last(searches));

const getHasCentralizedCashback = createSelector(getCorporate, corporate => corporate.has_cashback_centralized);

const getCorporateUserPermissions = createSelector(
  getCorporateUser,
  user => user.permissions_list
);

const checkPermission = (permissions, permissionRequired) => (
  !_.isEmpty(_.intersection(permissions, permissionRequired))
);

const hasRequisitionReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.REQUISTION_READ)
);

const hasRequisitionFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.REQUISTION_FULL)
);

const hasOrderPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.CLINIC_ORDER)
);

const hasCommitAndSaveReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.COMMIT_AND_SAVE_READ)
);

const hasCommitAndSaveFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.COMMIT_AND_SAVE_FULL)
);

const hasContractAuditReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.CONTRACT_AUDIT_READ)
);

const hasContractAuditFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.CONTRACT_AUDIT_FULL)
);

const hasInvoiceStatementReadPermissions = createSelector(
  getCorporateHasInvoiceStatementsEnabled,
  getCorporateUserPermissions,
  (hasInvoiceStatements, permissions) => (
    hasInvoiceStatements && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.INVOICE_STATEMENTS_READ)
  )
);

const hasInvoiceStatementFullPermissions = createSelector(
  getCorporateHasInvoiceStatementsEnabled,
  getCorporateUserPermissions,
  (hasInvoiceStatements, permissions) => (
    hasInvoiceStatements && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.INVOICE_STATEMENTS_FULL)
  )
);

const hasInvoiceReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.INVOICES_READ)
);

const hasInvoiceFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.INVOICES_FULL)
);

const hasClinicReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.CLINIC_INFO_READ)
);

const hasClinicFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.CLINIC_INFO_FULL)
);

const hasSSOManageReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.SSO_MANAGE_READ)
);

const hasSSOManageFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.SSO_MANAGE_FULL)
);

const hasSupplierReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.SUPPLIER_ACCOUNTS_READ)
);

const hasSupplierFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.SUPPLIER_ACCOUNTS_FULL)
);

const hasSavingsPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.MISSED_SAVINGS_READ)
);

const hasBudgetsReadPermissions = createSelector(
  getCorporateUserPermissions,
  getShowBudgets,
  (permissions, canSeeBudgets) => canSeeBudgets && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.BUDGETS_READ)
);

const hasBudgetsFullPermissions = createSelector(
  getCorporateUserPermissions,
  getShowBudgets,
  (permissions, canSeeBudgets) => canSeeBudgets && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.BUDGETS_FULL)
);

const hasAlertReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.ALERTS_READ)
);

const hasAlertFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.ALERTS_FULL)
);

const hasGlCodesFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.GLCODES_FULL)
);

const hasPeekPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.CLINIC_PEEK)
);

const hasCashbackPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.CASHBACK_READ)
);

const hasGuidanceReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.GUIDANCE_READ)
);

const hasGuidanceFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.GUIDANCE_FULL)
);

const hasRegionsReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.REGIONS_READ)
);

const hasRegionsFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.REGIONS_FULL)
);

const hasVendorBlacklistReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.VENDOR_BLACKLIST_READ)
);

const hasVendorBlacklistFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.VENDOR_BLACKLIST_FULL)
);

const hasManagePermissionsReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.MANAGE_PERMISSIONS_READ)
);

const hasManagePermissionsFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.MANAGE_PERMISSIONS_FULL)
);

const hasManageCorporateFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.MANAGE_CORPORATE_FULL)
);

const hasManageCorporateReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.MANAGE_CORPORATE_READ)
);

const hasMessagesFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.MESSAGES_FULL)
);

const hasMessagesReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.MESSAGES_READ)
);

const hasItemLinksFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.ITEM_LINKS_FULL)
);

const hasScriptCanAssignCallerPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.SCRIPT_ASSIGN_CALLER_FULL)
);

const hasPriceCompareReadPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.PRICE_COMPARE_READ)
);

const hasPriceCompareFullPermissions = createSelector(
  getCorporateUserPermissions,
  permissions => checkPermission(permissions, CORPORATE_PERMISSIONS_READ.PRICE_COMPARE_FULL)
);

const getCorporateScriptsEnabled = createSelector(
  getCorporate,
  corporate => corporate.scripts_enabled
);

const hasViewScriptsTabPermissions = createSelector(
  getCorporateScriptsEnabled,
  getCorporateUserPermissions,
  (scripts_enabled, permissions) => (
    scripts_enabled && (
      checkPermission(permissions, CORPORATE_PERMISSIONS_READ.SCRIPT_ASSIGN_CALLER_FULL)
      || checkPermission(permissions, CORPORATE_PERMISSIONS_READ.SCRIPT_BE_A_CALLER_FULL)
    )
  )
);

const hasOrderApprovalReadPermissions = createSelector(
  getCorporateUserPermissions,
  getIsCorporateKind,
  getHasApprovalOrdersEnabled,
  (permissions, isCorporateKind, hasApprovalOrdersEnabled) => (
    hasApprovalOrdersEnabled && isCorporateKind && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.APPROVAL_ORDERS_READ)
  )
);

const hasOrderApprovalFullPermissions = createSelector(
  getCorporateUserPermissions,
  getIsCorporateKind,
  getHasApprovalOrdersEnabled,
  (permissions, isCorporateKind, hasApprovalOrdersEnabled) => (
    hasApprovalOrdersEnabled && isCorporateKind && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.APPROVAL_ORDERS_FULL)
  )
);

const hasApprovalRuleReadPermissions = createSelector(
  getCorporateUserPermissions,
  getIsCorporateKind,
  getHasApprovalOrdersEnabled,
  (permissions, isCorporateKind, hasApprovalOrdersEnabled) => (
    hasApprovalOrdersEnabled && isCorporateKind && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.APPROVAL_RULES_READ)
  )
);

const hasApprovalRuleFullPermissions = createSelector(
  getCorporateUserPermissions,
  getIsCorporateKind,
  getHasApprovalOrdersEnabled,
  (permissions, isCorporateKind, hasApprovalOrdersEnabled) => (
    hasApprovalOrdersEnabled && isCorporateKind && checkPermission(permissions, CORPORATE_PERMISSIONS_READ.APPROVAL_RULES_FULL)
  )
);

const getShouldShowCommitAndSave = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__show_commit_and_save', false),
);

const getShowInvoicesDashboard = createSelector(
  getUISettings,
  settings => _.get(settings, 'invoices_dashboard_visible', true),
);

const hasGLCodedInvoicesPermissions = createSelector(
  getUISettings,
  settings => _.get(settings, 'show_invoice_gl_coded', false),
);

const hasInvoiceSentToPartnerPermissions = createSelector(
  getUISettings,
  settings => _.get(settings, 'sent_to_partner_visible', false)
);

const hasInvoiceItemsGLCodeBulkUpdatePermissions = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__show_invoice_items_gl_code_bulk_update', false),
);

const hasInvoicesPaymentPermissions = createSelector(
  getUISettings,
  settings => _.get(settings, 'show_invoice_payment', false),
);

const hasReceivablesReadPermissions = createSelector(
  getUISettings,
  settings => _.get(settings, 'item_receipts_visible'),
);

const hasInventoryCountsVisible = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__can_view_inventory_counts')
);

const hasCorporateInventoriedItemsVisible = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__show_corporate_inventoried_items', false)
);

const getHasInvoiceReceivingUI = createSelector(
  getUISettings,
  settings => _.get(settings, 'invoice_receiving_visible'),
);

const getHasInvoiceCanBeMarkedVoidUI = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__invoice_can_be_marked_void')
);

const getHasInvoiceCanBeMarkedSentToPartner = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__invoice_can_be_marked_processed_manually')
);

const getShowInvoiceStatusColumn = createSelector(
  getUISettings,
  settings => _.get(settings, 'show_invoice_status_column', true)
);

const hasPimsAggregatesVisible = createSelector(
  getUISettings,
  settings => _.get(settings, 'pims_aggregates_visible', false)
);

const hasAllRegionsPermissions = createSelector(
  getCorporateUser,
  user => user.has_all_regions_accessible
);

const getCanEditInvoiceItemGlCodesUI = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate_can_edit_invoice_item_glcodes'),
);

const getCorporateUsesSkusToAggregatePims = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate_pims__is_aggregated_by_sku'),
);

const getShowLocationIdInList = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate_show_location_ids_with_clinic_names'),
);

const getShowDirectCheckoutAnalytics = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate_show_direct_checkout_in_compliance_analytics'),
);

const getShowPriceCompares = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__can_view_price_compares', false)
);

const getCanDisabledAddToCartGuidance = createSelector(
  getUISettings,
  settings => _.get(settings, 'enable_guidance_to_disable_add_to_carts', false)
);

const getHasAutomatedOrderingEnabled = createSelector(
  getUISettings,
  settings => _.get(settings, 'has_automated_ordering_enabled', false),
);

const getCanExportApprovalOrders = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__can_export_approval_order_orders', false),
);

const getCanExportApprovalOrderItems = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__can_export_approval_orders_items', false),
);

const getShowSsoSettings = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__show_sso_settings', false),
);

const getValidUserSsoSigninStatuses = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__valid_user_sso_signin_statuses') || [],
);

const getHasSsoIntegration = createSelector(
  getUISettings,
  settings => _.get(settings, 'has_sso_integration', false),
);

const getCanSyncPimsToInventory = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__can_sync_pims_to_inventory', true),
);

const getBudgetAdjustmentsEnabled = createSelector(
  getUISettings,
  settings => _.get(settings, 'corporate__budget_adjustments_enabled', true)
);

const getShowDSBudgetTool = createSelector(
  getUISettings, settings => _.get(settings, 'corporate__show_ds_budget_tool', false)
);

const getCorporateHomeDeliveryAccess = createSelector(
  getUISettings, settings => _.get(settings, 'corporate__show_home_delivery_access', false)
);

const hasCommitAndSaveReadAccess = createSelector(
  getShouldShowCommitAndSave,
  hasCommitAndSaveReadPermissions,
  (shouldShowCommitAndSave, hasReadPermissions) => shouldShowCommitAndSave && hasReadPermissions
);

const hasCommitAndSaveFullAccess = createSelector(
  getShouldShowCommitAndSave,
  hasCommitAndSaveFullPermissions,
  (shouldShowCommitAndSave, hasFullPermissions) => shouldShowCommitAndSave && hasFullPermissions
);

const hasSSOManageReadAccess = createSelector(
  getShowSsoSettings,
  hasSSOManageReadPermissions,
  (showSsoSettings, hasSSOManagePermissions) => showSsoSettings && hasSSOManagePermissions
);

const hasSSOManageFullAccess = createSelector(
  getShowSsoSettings,
  hasSSOManageFullPermissions,
  (showSsoSettings, hasSSOManagePermissions) => showSsoSettings && hasSSOManagePermissions
);

const hasInvoiceItemGlCodesFullPermissions = createSelector(
  hasGlCodesFullPermissions,
  getCanEditInvoiceItemGlCodesUI,
  (hasGlCodesPermissions, canEditInvoiceItemGlCodes) => hasGlCodesPermissions && canEditInvoiceItemGlCodes
);

const hasInvoiceItemsGLCodeBulkUpdateAccess = createSelector(
  hasInvoiceItemGlCodesFullPermissions,
  hasInvoiceItemsGLCodeBulkUpdatePermissions,
  (hasInvoiceItemGlCodesPermissions, hasInvoiceItemsGLCodeBulkPermissions) => (
    hasInvoiceItemGlCodesPermissions && hasInvoiceItemsGLCodeBulkPermissions
  )
);

const getRegionsWithAccess = createSelector(
  getRegions,
  getCorporateUser,
  hasAllRegionsPermissions,
  (regions, user, hasPermissions) => {
    if (hasPermissions) {
      return regions;
    }
    return _.filter(regions, r => _.includes(user.regions_accessible, r.id));
  }
);

const getRegionsToChoose = createSelector(getRegions, regions => (
  _.orderBy(_.map(regions, r => ({ label: r.name, value: r.id })), [region => region.label.toLowerCase()])
));

const getRestrictedRegionsToChoose = createSelector(getRegionsWithAccess, regions => (
  _.orderBy(_.map(regions, r => ({ label: r.name, value: r.id })), [region => region.label.toLowerCase()])
));

const getRestrictedPermissions = createSelector(
  getIsPunchoutAvailable,
  getCorporateScriptsEnabled,
  (isPunchoutAvailable, scriptsEnabled) => (
    {
      [BUYER_PERMISSIONS_OBJECT.PUNCHOUTS.value]: isPunchoutAvailable,
      [BUYER_PERMISSIONS_OBJECT.SCRIPTS_READ.value]: scriptsEnabled
    }
  )
);

const getBuyerPermissionGroups = createSelector(
  getIsPathWayOrThrive,
  getRestrictedPermissions,
  (isPathWayOrThrive, restrictedPermissions) => {
    const groupsToPermissions = isPathWayOrThrive ? PATHWAY_THRIVE_CUSTOM_GROUPS : PERMISSION_GROUPS;
    return _.mapValues(groupsToPermissions, group => ({
      ...group,
      permissions: getFilteredPermissions(
        group.permissions,
        restrictedPermissions
      )
    }));
  }
);

const getAvailablePermissions = createSelector(
  getRestrictedPermissions,
  restrictedPermissions => getFilteredPermissions(
    BUYER_PERMISSIONS_ALPHABETICAL,
    restrictedPermissions,
    'value'
  )
);

export const getBuyerRegionLookup = createSelector(
  getRegionsWithAccess,
  (regions) => {
    const lookup = {};
    _.forEach(
      regions,
      region => (
        _.forEach(
          _.get(region, 'buyer_ids', []),
          buyerId => lookup[buyerId] = _.get(region, 'name', '')
        )
      )
    );
    return lookup;
  }
);

const getRegionIdToLabelMapping = createSelector(getRegions, regions => (
  _.mapValues(_.keyBy(regions, 'id'), 'name')
));

const getNumberOfBuyers = createSelector(
  getRegionsWithAccess,
  getBuyers,
  hasAllRegionsPermissions,
  (regions, buyers, hasPermissions) => {
    if (hasPermissions) {
      return _.size(buyers);
    }
    return _.sumBy(regions, r => _.size(r.buyer_ids));
  }
);

const getBuyersInRegions = createSelector(
  getRegionsWithAccess,
  getBuyers,
  hasAllRegionsPermissions,
  (regions, buyers, hasPermissions) => {
    if (hasPermissions) {
      return buyers;
    }
    const buyer_ids = _.flattenDeep(_.map(regions, 'buyer_ids'));
    return _.filter(buyers, buyer => _.includes(buyer_ids, buyer.id));
  }
);

const getRegionIdFromProps = (state, props) => props.regionId;
const getBuyersInRegion = createSelector(
  getRegionsWithAccess,
  getBuyersInRegions,
  getRegionIdFromProps,
  (regions, buyers, regionId) => {
    if (!regionId) {
      return buyers;
    }
    const regionIdToRegion = _.keyBy(regions, 'id');
    const buyer_ids = _.get(regionIdToRegion, [regionId, 'buyer_ids']);
    return _.filter(buyers, buyer => _.includes(buyer_ids, buyer.id));
  }
);

const getRegionIdsFromProps = (state, props) => props.regionIds;
const getBuyersInSpecificRegions = createSelector(
  getRegionsWithAccess,
  getBuyersInRegions,
  getRegionIdsFromProps,
  (regions, buyers, regionIds) => {
    if (_.isEmpty(regionIds)) {
      return buyers;
    }
    regionIds = _.split(regionIds, ',');
    const regionIdToRegion = _.keyBy(regions, 'id');
    const buyer_ids = _.flatMap(regionIds, regionId => _.get(regionIdToRegion, [regionId, 'buyer_ids']));
    return _.filter(buyers, buyer => _.includes(buyer_ids, buyer.id));
  }
);

/**
 * Selector for the searchbar with formatting
 */
const getSearchSuggestions = createSelector(
  getBuyersInRegions,
  buyers => (
    !_.isEmpty(buyers) ? _.map(buyers, buyer => ({ ...buyer, type: 'hospital' })) : null
  )
);

const getFilteredGuidanceRules = createSelector(
  getCanDisabledAddToCartGuidance,
  (canDisabledAddToCartGuidance) => {
    const guidanceRules = getGuidanceRules();
    if (canDisabledAddToCartGuidance) {
      return guidanceRules;
    }
    return _.filter(
      guidanceRules,
      rule => rule.key !== 'disable_add_to_carts'
    );
  }
);

const getSortedGlCodes = createSelector(
  getGlCodes,
  glcodes => _.sortBy(glcodes, ['code_kind', 'code'])
);

const getSortedGlCodesAll = createSelector(
  getGlCodesAll,
  glcodes => _.sortBy(glcodes, ['code_kind', 'code'])
);


export {
  getCorporate,
  getCorporateDataSettings,
  getCorporateUser,
  getProfile,
  getRegion,
  getRegions,
  getDashboard,
  getSearchSuggestions,
  getSortedBuyersForOrdersInvoices,
  getManufacturerSearchResults,
  getManufacturerSearchQuery,
  getProductSearchResults,
  getProductSearchQuery,
  getBuyers,
  getBuyersInRegions,
  getBuyersInRegion,
  getSuppliers,
  getSupplier,
  getIsBuyersFetched,
  getEmails,
  getGlCodes,
  getGlCodesAll,
  getSortedGlCodes,
  getSortedGlCodesAll,
  getProductLines,
  getManufacturers,
  getRequisitionApprovalPermission,
  getGroupedCategories,
  getIsCorporateKind,
  getIsAdminView,
  getIsStaffOrAdminView,
  hasRequisitionReadPermissions,
  hasRequisitionFullPermissions,
  hasOrderPermissions,
  hasCommitAndSaveReadPermissions,
  hasCommitAndSaveFullPermissions,
  hasCommitAndSaveReadAccess,
  hasCommitAndSaveFullAccess,
  hasContractAuditReadPermissions,
  hasContractAuditFullPermissions,
  hasGlCodesFullPermissions,
  hasInvoiceStatementReadPermissions,
  hasInvoiceStatementFullPermissions,
  hasInvoiceReadPermissions,
  hasInvoiceFullPermissions,
  hasClinicReadPermissions,
  hasSupplierReadPermissions,
  hasSupplierFullPermissions,
  hasSavingsPermissions,
  hasBudgetsReadPermissions,
  hasBudgetsFullPermissions,
  hasAlertReadPermissions,
  hasAlertFullPermissions,
  hasPeekPermissions,
  hasCashbackPermissions,
  hasGuidanceReadPermissions,
  hasGuidanceFullPermissions,
  hasRegionsReadPermissions,
  hasRegionsFullPermissions,
  hasClinicFullPermissions,
  hasSSOManageReadPermissions,
  hasSSOManageFullPermissions,
  hasSSOManageReadAccess,
  hasSSOManageFullAccess,
  hasVendorBlacklistReadPermissions,
  hasVendorBlacklistFullPermissions,
  hasManagePermissionsReadPermissions,
  hasManagePermissionsFullPermissions,
  hasManageCorporateFullPermissions,
  hasMessagesReadPermissions,
  hasMessagesFullPermissions,
  hasManageCorporateReadPermissions,
  hasPriceCompareReadPermissions,
  hasReceivablesReadPermissions,
  getRegionsWithAccess,
  getNumberOfBuyers,
  checkPermission,
  getRegionsToChoose,
  getRestrictedRegionsToChoose,
  getRegionIdToLabelMapping,
  getShowInvoiceStatusColumn,
  getShowSsoSettings,
  getValidUserSsoSigninStatuses,
  hasAllRegionsPermissions,
  hasOrderApprovalFullPermissions,
  hasOrderApprovalReadPermissions,
  hasApprovalRuleFullPermissions,
  hasApprovalRuleReadPermissions,
  hasInventoryCountsVisible,
  hasCorporateInventoriedItemsVisible,
  getHasInvoiceReceivingUI,
  getHasInvoiceCanBeMarkedVoidUI,
  hasPimsAggregatesVisible,
  hasScriptCanAssignCallerPermissions,
  hasViewScriptsTabPermissions,
  getUISettings,
  getAnalyzeStartDateParam,
  hasInvoiceItemGlCodesFullPermissions,
  getShowLocationIdInList,
  hasGLCodedInvoicesPermissions,
  hasInvoiceSentToPartnerPermissions,
  getHasInvoiceCanBeMarkedSentToPartner,
  getHasApprovalOrdersEnabled,
  getGlCodesWithName,
  getShowDirectCheckoutAnalytics,
  getAllSuppliersHash,
  getBuyerPermissionGroups,
  getFilteredGuidanceRules,
  getCorporateHasDataSyncingEnabled,
  getCanViewReceivableWarnings,
  getShowDSBudgetTool,
  getBuyersInSpecificRegions,
  getAvailablePermissions,
};

export const useHasCentralizedCashback = () => useSelector(getHasCentralizedCashback);
export const useIsBuyersFetched = () => useSelector(getIsBuyersFetched);
export const useIsSuppliersFetched = () => useSelector(getIsSuppliersFetched);
export const useCorporate = () => useSelector(getCorporate);
export const useCorporateDataSettings = () => useSelector(getCorporateDataSettings);
export const useCorporateUser = () => useSelector(getCorporateUser);
export const useProfile = () => useSelector(getProfile);
export const useBuyers = () => useSelector(getBuyers);
export const useBuyer = buyerId => useSelector(state => getBuyer(state, { buyerId }));
export const useDashboard = () => useSelector(getDashboard);
export const useDashboardFetches = () => useSelector(getDashboardFetches);
export const useDashboardBuyer = buyerId => useSelector(state => getDashboardBuyer(state, { buyerId }));
export const useSuppliers = () => useSelector(getSuppliers);
export const useAllSuppliersHash = () => useSelector(getAllSuppliersHash);
export const useSupplier = supplierId => useSelector(state => getSupplier(state, { supplierId }));
export const useRequisitionApprovalPermission = () => useSelector(getRequisitionApprovalPermission);
export const useGlCodes = () => useSelector(getGlCodes);
export const useGlCodesAll = () => useSelector(getGlCodesAll);
export const useSortedGlCodes = () => useSelector(getSortedGlCodes);
export const useSortedGlCodesAll = () => useSelector(getSortedGlCodesAll);
export const useGlCodesLookupById = glCodeId => useSelector(
  state => getGlCodesLookupById(state, { glCodeId })
);
export const useGlCodesAllLookupById = glCodeId => useSelector(
  state => getGlCodesAllLookupById(state, { glCodeId })
);
export const useGlCodesLookup = () => useSelector(getGlCodesLookup);
export const useGlCodesAllLookup = () => useSelector(getGlCodesAllLookup);
export const useGlCodesByKind = () => useSelector(getGlCodesByKind);
export const useCorporateCompanies = () => useSelector(getCorporateCompanies);
export const useCorporateCostCenters = () => useSelector(getCorporateCostCenters);
export const useCategories = () => useSelector(getCategories);
export const useUncodedCategories = () => useSelector(getUncodedCategories);
export const useIsCorporateKind = () => useSelector(getIsCorporateKind);
export const useSupplierNames = supplierIds => useSelector(state => getSupplierNames(state, { supplierIds }));
export const useIsAdminView = () => useSelector(getIsAdminView);
export const useIsStaffOrAdminView = () => useSelector(getIsStaffOrAdminView);
export const useManufacturerSearchResults = queryParams => useSelector(
  state => getManufacturerSearchResults(state, { queryParams })
);
export const useManufacturerSearchQuery = () => useSelector(getManufacturerSearchQuery);
export const useProductSearchResults = queryParams => useSelector(
  state => getProductSearchResults(state, { queryParams })
);
export const useProductSearchQuery = () => useSelector(getProductSearchQuery);


export const useRequisitionFullPermissions = () => useSelector(hasRequisitionFullPermissions);
export const useInvoiceStatementReadPermissions = () => useSelector(hasInvoiceStatementReadPermissions);
export const useInvoiceStatementFullPermissions = () => useSelector(hasInvoiceStatementFullPermissions);
export const useInvoiceFullPermissions = () => useSelector(hasInvoiceFullPermissions);
export const useClinicFullPermissions = () => useSelector(hasClinicFullPermissions);
export const useSSOManageReadPermissions = () => useSelector(hasSSOManageReadPermissions);
export const useSSOManageFullPermissions = () => useSelector(hasSSOManageFullPermissions);
export const useVendorBlacklistFullPermissions = () => useSelector(hasVendorBlacklistFullPermissions);
export const useManagePermissionsFullPermissions = () => useSelector(hasManagePermissionsFullPermissions);
export const useManagePermissionsReadPermissions = () => useSelector(hasManagePermissionsReadPermissions);
export const useGuidanceReadPermissions = () => useSelector(hasGuidanceReadPermissions);
export const useRegionsReadPermissions = () => useSelector(hasRegionsReadPermissions);
export const useVendorBlacklistReadPermissions = () => useSelector(hasVendorBlacklistReadPermissions);
export const useClinicReadPermissions = () => useSelector(hasClinicReadPermissions);
export const useAlertReadPermissions = () => useSelector(hasAlertReadPermissions);
export const useBudgetsFullPermissions = () => useSelector(hasBudgetsFullPermissions);
export const useCashbackPermissions = () => useSelector(hasCashbackPermissions);
export const useSupplierFullPermissions = () => useSelector(hasSupplierFullPermissions);
export const useMessagesFullPermissions = () => useSelector(hasMessagesFullPermissions);
export const useMessagesReadPermissions = () => useSelector(hasMessagesReadPermissions);
export const usePriceCompareReadPermissions = () => useSelector(hasPriceCompareReadPermissions);
export const usePriceCompareFullPermissions = () => useSelector(hasPriceCompareFullPermissions);
export const useRegion = () => useSelector(getRegion);
export const useRegions = () => useSelector(getRegions);
export const useRegionsToChoose = () => useSelector(getRegionsToChoose);
export const useRestrictedRegionsToChoose = () => useSelector(getRestrictedRegionsToChoose);
export const useRegionIdToLabelMapping = () => useSelector(getRegionIdToLabelMapping);
export const useRegionsWithAccess = () => useSelector(getRegionsWithAccess);
export const useAllRegionsPermissions = () => useSelector(hasAllRegionsPermissions);
export const useBuyerRegionLookup = () => useSelector(getBuyerRegionLookup);
export const useReceivablesReadPermissions = () => useSelector(hasReceivablesReadPermissions);
export const useOrderApprovalReadPermissions = () => useSelector(hasOrderApprovalReadPermissions);
export const useOrderApprovalFullPermissions = () => useSelector(hasOrderApprovalFullPermissions);
export const useApprovalRuleFullPermissions = () => useSelector(hasApprovalRuleFullPermissions);
export const useApprovalRuleReadPermissions = () => useSelector(hasApprovalRuleReadPermissions);
export const useItemLinkFullPermissions = () => useSelector(hasItemLinksFullPermissions);
export const useScriptCanAssignCallerPermissions = () => useSelector(hasScriptCanAssignCallerPermissions);
export const useViewScriptsTabPermissions = () => useSelector(hasViewScriptsTabPermissions);
export const useUISettings = () => useSelector(getUISettings);
export const useShouldShowCommitAndSave = () => useSelector(getShouldShowCommitAndSave);
export const useShowInvoicesDashboard = () => useSelector(getShowInvoicesDashboard);
export const useCanOverReceive = () => useSelector(getCanOverReceive);
export const useHasGLCodedInvoicesPermissions = () => useSelector(hasGLCodedInvoicesPermissions);
export const useHasInvoiceSentToPartnerPermissions = () => useSelector(hasInvoiceSentToPartnerPermissions);
export const useHasInvoiceItemsGLCodeBulkUpdateAccess = () => useSelector(hasInvoiceItemsGLCodeBulkUpdateAccess);
export const useHasInvoicesPaymentPermissions = () => useSelector(hasInvoicesPaymentPermissions);
export const useHasGlCodesFullPermissions = () => useSelector(hasGlCodesFullPermissions);
export const useValidUserSsoSigninStatuses = () => useSelector(getValidUserSsoSigninStatuses);
export const useHasCommitAndSaveReadAccess = () => useSelector(hasCommitAndSaveReadAccess);
export const useHasCommitAndSaveFullAccess = () => useSelector(hasCommitAndSaveFullAccess);
export const useSSOManageReadAccess = () => useSelector(hasSSOManageReadAccess);
export const useSSOManageFullAccess = () => useSelector(hasSSOManageFullAccess);
export const useHasInvoiceItemGlCodesFullPermissions = () => useSelector(hasInvoiceItemGlCodesFullPermissions);
export const useBuyerPermissionGroups = () => useSelector(getBuyerPermissionGroups);
export const useGetCorporateUsesSkusToAggregatePims = () => useSelector(getCorporateUsesSkusToAggregatePims);
export const useHasSecondLocationId = () => useSelector(getHasSecondLocationId);
export const useShowPriceCompares = () => useSelector(getShowPriceCompares);
export const useHasAutomatedOrderingEnabled = () => useSelector(getHasAutomatedOrderingEnabled);
export const useGetCanExportApprovalOrders = () => useSelector(getCanExportApprovalOrders);
export const useGetCanExportApprovalOrderItems = () => useSelector(getCanExportApprovalOrderItems);
export const useHasSsoIntegration = () => useSelector(getHasSsoIntegration);
export const useEmail = () => useSelector(getEmail);
export const useInviteFieldsRequirementInfo = () => useSelector(getInviteFieldsRequirementInfo);
export const useCanSyncPimsToInventory = () => useSelector(getCanSyncPimsToInventory);
export const useCanViewReceivableWarnings = () => useSelector(getCanViewReceivableWarnings);
export const useBudgetAdjustmentsEnabled = () => useSelector(getBudgetAdjustmentsEnabled);
export const useShowDSBudgetTool = () => useSelector(getShowDSBudgetTool);
export const useBuyersInRegion = regionId => useSelector(state => getBuyersInRegion(state, { regionId }));
export const useCorporateHomeDeliveryAccess = () => useSelector(getCorporateHomeDeliveryAccess);
export const useShowCorporateInventoriedItems = () => useSelector(hasCorporateInventoriedItemsVisible);
export const useAvailablePermissions = () => useSelector(getAvailablePermissions);
export const useIsPunchoutAvailable = () => useSelector(getIsPunchoutAvailable);
export const useRestrictedPermissions = () => useSelector(getRestrictedPermissions);
export const useBuyerCorporateMetaOptions = () => useSelector(getBuyerCorporateMetaOptions);
